import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import inductionService from '../../services/induction';
import type { Operators } from '../../types/induction';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

interface Props {
  equipmentId: number;
  /** An assessor seat: links through to sign people off. */
  canAssess: boolean;
}

/**
 * Who may operate this machine: everyone with a current sign-off on every
 * induction it requires. Read from the same requirement the job and
 * pre-start gates use, so the list and the gate cannot disagree.
 */
export default function EquipmentOperatorsCard({ equipmentId, canAssess }: Props) {
  const orgTz = useOrgTimezone();
  const [data, setData] = useState<Operators | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    inductionService.operators(equipmentId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [equipmentId]);

  if (failed || !data) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-5 w-5 text-yellow-700 shrink-0" /> Who may operate this
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {data.inductions.length === 0 ? (
          <p className="text-neutral-500">No induction is required for this machine.</p>
        ) : (
          <>
            <p className="text-neutral-600 break-words">
              Requires: {data.inductions.map((i, idx) => (
                <span key={i.id}>
                  {idx > 0 && ', '}
                  <Link to={`/my-inductions/equipment/${i.id}`} className="text-primary-700 hover:underline">{i.title}</Link>
                </span>
              ))}
              {!data.enforce_equipment && ' (not enforced — switched off for this organisation)'}
            </p>
            {data.operators.length === 0 ? (
              <p className="rounded-lg bg-warning-50 p-3 text-warning-700">
                Nobody is signed off on this machine yet, so nobody can be put on a job with it.
              </p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {data.operators.map(o => (
                  <li key={o.ref} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    {canAssess ? (
                      <Link to={`/inductions/people/${o.ref}`} className="min-w-0 break-words font-medium text-neutral-800 hover:underline">
                        {o.name || 'Unnamed'}
                      </Link>
                    ) : (
                      <span className="min-w-0 break-words font-medium text-neutral-800">{o.name || 'Unnamed'}</span>
                    )}
                    {o.expires_on && (
                      <span className={o.status === 'expiring' ? 'text-xs text-warning-700' : 'text-xs text-neutral-500'}>
                        until {formatLocalDateInTz(o.expires_on, orgTz)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canAssess && data.inductions[0] && (
              <Link to={`/inductions/equipment/${data.inductions[0].id}`} className="inline-block text-primary-700 hover:underline">
                Sign people off
              </Link>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
